import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Shield, FileText, CheckCircle, ArrowLeft, Tag, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function OrderPage49() {
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');
  const [promoEmail, setPromoEmail] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validating, setValidating] = useState(false);

  const basePrice = 49;
  const discountedPrice = appliedDiscount
    ? Math.round(basePrice * (1 - appliedDiscount.discount_pct / 100) * 100) / 100
    : basePrice;

  const validatePromo = async () => {
    if (!promoCode.trim() || !promoEmail.trim()) {
      toast.error('Veuillez renseigner votre email et le code');
      return;
    }
    setValidating(true);
    try {
      const res = await axios.post(`${API_URL}/api/discount/validate`, {
        code: promoCode.trim(),
        email: promoEmail.trim()
      });
      setAppliedDiscount(res.data);
      sessionStorage.setItem('client_email', promoEmail.trim());
      toast.success(`Code appliqué : -${res.data.discount_pct}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code invalide ou non associé à cet email');
      setAppliedDiscount(null);
    } finally {
      setValidating(false);
    }
  };

  const removePromo = () => {
    setAppliedDiscount(null);
    setPromoCode('');
  };

  const handleProceed = () => {
    sessionStorage.setItem('orderStarted', 'true');
    sessionStorage.setItem('basePrice', discountedPrice.toString());
    if (appliedDiscount) {
      sessionStorage.setItem('discount_code', promoCode.trim().toUpperCase());
      sessionStorage.setItem('discount_pct', appliedDiscount.discount_pct.toString());
    } else {
      sessionStorage.removeItem('discount_code');
      sessionStorage.removeItem('discount_pct');
    }
    navigate('/upsell');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="container-narrow py-4 flex justify-between items-center">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour</span>
          </button>
          <Badge variant="outline" className="text-xs">
            Suisse romande
          </Badge>
        </div>
      </header>

      <div className="py-12 sm:py-16">
        <div className="container-narrow max-w-3xl">
          {/* En-tête */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
              <Shield className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4" data-testid="order-title">
              Commandez votre courrier administratif
            </h1>
            <p className="text-lg text-muted-foreground">
              Service professionnel de rédaction basé sur le droit suisse
            </p>
          </div>

          {/* Carte Prix */}
          <Card className="p-5 sm:p-8 border-2 border-primary/20 mb-8" data-testid="pricing-card">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
              <div>
                <Badge className="mb-3 bg-[hsl(var(--gold))] text-white">Service de base</Badge>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">
                  Courrier administratif complet
                </h2>
                <p className="text-muted-foreground text-sm">
                  Rédaction professionnelle conforme au droit suisse
                </p>
              </div>
              <div className="sm:text-right flex-shrink-0">
                <div className="text-3xl sm:text-4xl font-bold text-accent" data-testid="base-price">
                  CHF 49
                </div>
                <p className="text-xs text-muted-foreground mt-1">TTC</p>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="space-y-3">
              <h3 className="font-semibold text-foreground mb-4">Ce qui est inclus :</h3>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Analyse de votre situation</p>
                  <p className="text-sm text-muted-foreground">Étude approfondie de votre problématique administrative</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Rédaction structurée</p>
                  <p className="text-sm text-muted-foreground">Courrier conforme au Code civil et Code des obligations suisses</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Format professionnel</p>
                  <p className="text-sm text-muted-foreground">Courrier prêt à imprimer et envoyer aux autorités</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Livraison rapide</p>
                  <p className="text-sm text-muted-foreground">Courrier finalisé sous 24-48h par email (PDF)</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Révision possible</p>
                  <p className="text-sm text-muted-foreground">Une demande de modification incluse après livraison</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Garanties */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <Card className="p-4 text-center">
              <Shield className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-xs font-medium text-foreground">Confidentiel</p>
              <p className="text-xs text-muted-foreground mt-1">Données sécurisées</p>
            </Card>
            <Card className="p-4 text-center">
              <FileText className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-xs font-medium text-foreground">Conforme</p>
              <p className="text-xs text-muted-foreground mt-1">Droit suisse</p>
            </Card>
            <Card className="p-4 text-center">
              <CheckCircle className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-xs font-medium text-foreground">Rapide</p>
              <p className="text-xs text-muted-foreground mt-1">24-48h</p>
            </Card>
          </div>

          {/* Code promo */}
          <Card className="p-4 mb-6" data-testid="promo-code-section">
            {appliedDiscount ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-700">
                    {promoCode.toUpperCase()} — {appliedDiscount.discount_pct}% de réduction
                  </span>
                </div>
                <button onClick={removePromo} className="text-muted-foreground hover:text-destructive" data-testid="remove-promo">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Code de réduction"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="flex-1 h-10"
                    data-testid="promo-code-input"
                  />
                  <Button
                    variant="outline"
                    onClick={validatePromo}
                    disabled={validating || !promoCode.trim() || !promoEmail.trim()}
                    className="h-10 px-4"
                    data-testid="apply-promo-button"
                  >
                    {validating ? '...' : 'Appliquer'}
                  </Button>
                </div>
                {promoCode.trim() && (
                  <Input
                    type="email"
                    placeholder="Votre email (associé au code)"
                    value={promoEmail}
                    onChange={(e) => setPromoEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && validatePromo()}
                    className="h-10"
                    data-testid="promo-email-input"
                  />
                )}
              </div>
            )}
          </Card>

          {/* CTA */}
          <Button
            size="lg"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 text-base sm:text-lg font-semibold"
            onClick={handleProceed}
            data-testid="proceed-button"
          >
            {appliedDiscount
              ? `Continuer — CHF ${discountedPrice} (au lieu de CHF 49)`
              : 'Continuer — CHF 49'
            }
          </Button>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Paiement sécurisé · Aucun engagement · Satisfaction garantie
          </p>

          {/* Disclaimer */}
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-900">
              <strong>Assistance rédactionnelle uniquement</strong> — Ce service ne constitue pas un conseil juridique. 
              Les courriers générés ne sont pas révisés par des avocats. 
              Pour toute question juridique complexe, veuillez consulter un professionnel du droit.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderPage49;
